import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { Loader2 } from "lucide-react";
import ErrorBoundary from "./ErrorBoundary";

const ONBOARDING_PATHS = ["/onboarding", "/login", "/"];

function isOnboardingExempt(pathname) {
  return ONBOARDING_PATHS.includes(pathname) || pathname.startsWith("/shared/");
}

async function resolveOnboardingStep(userData) {
  // If already complete, skip the round-trip.
  if (userData.onboarding_complete !== false) return "complete";
  // Call the backend status endpoint — it applies all soft-cutover logic server-side
  // and may auto-mark legacy users as complete.
  const { data } = await api.get("/onboarding/status");
  return data.step;
}

export default function ProtectedRoute({ children, adminOnly = false }) {
  const { user, setUser, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [verified, setVerified] = useState(location.state?.user ? true : null);

  useEffect(() => {
    if (location.state?.user) { setVerified(true); return; }
    if (loading) return;
    (async () => {
      try {
        let userData = user;
        if (!userData) {
          const { data } = await api.get("/auth/me");
          userData = data;
          setUser(data);
        }

        // Check onboarding — call status endpoint for authoritative result
        if (!isOnboardingExempt(location.pathname) && userData.onboarding_complete === false) {
          const step = await resolveOnboardingStep(userData);
          if (step !== "complete") {
            navigate("/onboarding", { replace: true });
            return;
          }
          // Backend auto-completed (legacy user) — re-fetch so user state is fresh
          const { data: freshUser } = await api.get("/auth/me");
          setUser(freshUser);
        }

        if (adminOnly && !userData.is_admin) {
          navigate((userData.role === "parent" || userData.is_parent) ? "/parent" : "/dashboard", { replace: true });
          return;
        }
        setVerified(true);
      } catch {
        navigate("/login", { replace: true });
      }
    })();
  }, [user, setUser, loading, navigate, location.state, location.pathname, adminOnly]);

  if (verified !== true) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
      </div>
    );
  }
  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>;
}
